import { Injectable } from '@angular/core';
import { LogService } from '@pershing/pershing-core';
import { TradingEntitlements } from '../../models/common/trading-entitlements';
import { TransactionType, OrderType, OrderDuration, TradeExchange, AddlOrdData, TradingExchangeOrder, TradeType, UserType } from '../../models/common/trading-common.model';
/**
 * @desc Get the AdditionalOrderDataCompliance for Equity and Options.
 *
 */
@Injectable()
export class ComplianceService {
    tradingEntitlements: TradingEntitlements;
    INVALID_AON_FOR_EQUITY: string = 'INVALID_AON_FOR_EQUITY';
    INVALID_NOTHELD_FOR_EQUITY: string = 'INVALID_NOTHELD_FOR_EQUITY';
    INVALID_DONOTREDUCE_FOR_EQUITY: string = 'INVALID_DONOTREDUCE_FOR_EQUITY';
    INVALID_DONOTINCREASE_FOR_EQUITY: string = 'INVALID_DONOTINCREASE_FOR_EQUITY';
    INVALID_CASHSAMEDAY_FOR_EQUITY: string = 'INVALID_CASHSAMEDAY_FOR_EQUITY';
    INVALID_NEXTDAY_FOR_EQUITY: string = 'INVALID_NEXTDAY_FOR_EQUITY';
    INVALID_EXECUTEASAGENT_FOR_EQUITY: string = 'INVALID_EXECUTEASAGENT_FOR_EQUITY';

    INVALID_AON_FOR_OPTION: string = 'INVALID_AON_FOR_OPTION';
    INVALID_NOTHELD_FOR_OPTION: string = 'INVALID_NOTHELD_FOR_OPTION';
    INVALID_EXECUTEASAGENT_FOR_OPTION: string = 'INVALID_EXECUTEASAGENT_FOR_OPTION';

    invalidOrderTypeListForAON: string[] = [OrderType.TRAILING_STOP_DOLLAR, OrderType.TRAILING_STOP_PCT, OrderType.TRAILING_STOP_LIMIT_DOLLAR,
    OrderType.TRAILING_STOP_LIMIT_PCT, OrderType.STOP, OrderType.STOP_LIMIT, OrderType.MARKET];
    allowedOrderTypeListForNH: string[] = [OrderType.MARKET, OrderType.LIMIT,OrderType.NET_CREDIT,OrderType.NET_DEBIT,OrderType.EVEN, OrderType.LIMIT_ON_CLOSE];
    orderTypeListNHQuantity: string[] = [OrderType.MARKET, OrderType.LIMIT,OrderType.NET_CREDIT,OrderType.NET_DEBIT,OrderType.EVEN];
    durationListAONQuantity: string[] = [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM];
    durationListForGoodTill: string[] = [OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM];
    durationListForGtcGtd: string[] = [OrderDuration.GTC, OrderDuration.GTD];
    allowedDurationListForNH: string[] = [OrderDuration.DAY];
    notAllowedDurationListForQuantity: string[] = [OrderDuration.FOK, OrderDuration.IOC];
    invalidDurationListForDNIAndRIA: string[] = [OrderDuration.GTD, OrderDuration.GTM];
    invalidOrderTypeListForCSD: string[] = [OrderType.STOP, OrderType.STOP_LIMIT, OrderType.TRAILING_STOP_DOLLAR, OrderType.TRAILING_STOP_PCT,
    OrderType.TRAILING_STOP_LIMIT_DOLLAR, OrderType.TRAILING_STOP_LIMIT_PCT];
    allowedDurationListForCSDA: string[] = [OrderDuration.DAY, OrderDuration.OPG];
    orderTypeListForCSDAndEAA: string[] = [OrderType.LIMIT, OrderType.EXECUTE_AT_CLOSE, OrderType.LIMIT_ON_CLOSE];
    orderTypeListForNDSAndEAA: string[] = [OrderType.MARKET, OrderType.LIMIT, OrderType.EXECUTE_AT_CLOSE, OrderType.LIMIT_ON_CLOSE];
    tradeExchangeListForOTC: string[] = [TradeExchange.NASD, TradeExchange.NASD_BB, TradeExchange.NASD_SC, TradeExchange.NASD_NM, TradeExchange.NASD_STR, TradeExchange.NNQS];
    sellTransactionTypeList: string[] = [TransactionType.SELL_FROM_PORTFOLIO, TransactionType.SELL_NOT_IN_ACCOUNT,
    TransactionType.SELL_SHORT, TransactionType.SELL_ALL];
    buyTransactionTypeList: string[] = [TransactionType.BUY, TransactionType.BUY_TO_COVER];
    orderTypeListForLimits: string[] = [OrderType.LIMIT, OrderType.LIMIT_ON_CLOSE];
    optionsAllowedDurationListAON: string[] = [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM, OrderDuration.FOK];
    optionsAllowedOrderTypeListNH: string[] = [OrderType.MARKET, OrderType.NET_CREDIT, OrderType.NET_DEBIT, OrderType.LIMIT, OrderType.STOP];
    optionsAllowedDurationListEAA: string[] = [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM, OrderDuration.FOK, OrderDuration.IOC];
    optionsAllowedOrderTypeListEAA: string[] = [OrderType.MARKET, OrderType.LIMIT, OrderType.STOP, OrderType.STOP_LIMIT];

    isDoNotReduceRequired: boolean = false;
    isOrderQualifierEnable: boolean = false;
    isAonAvailable: boolean = true;
    isNotHeldAvailable: boolean = true;
    isDoNotIncreaseAvailable: boolean = true;
    isDoNoReduceAvailable: boolean = true;
    isNextDayAvailable: boolean = true;
    isCashSameDayAvailable: boolean = true;
    isExecuteAsagentAvailable: boolean = true;

    isDoNotIncreaseDisabled: boolean = false;
    isDoNotReduceDisabled: boolean = false;
    isNextDayDisabled: boolean = false;
    isCashSameDayDisabled: boolean = false;
    constructor(private logService: LogService) {
    }

/**
	* Runs through the different compliance checks for additional order information
	* Return true if everything is OK and there is NO compliance
	* Return false if there is a compliance
	*/
    public doCompliance(tradingExchangeOrder: TradingExchangeOrder): boolean {
        if (((tradingExchangeOrder.addlOrdData === null)
            || tradingExchangeOrder.addlOrdData.length === 0)) {
            this.logService.info('Additional Order Data is empty, skipping AddlOrdDataCompliance');
            return true;
        }
        if (tradingExchangeOrder.errors == null) {
            tradingExchangeOrder.errors = [];
        }

        if (tradingExchangeOrder.warnings == null) {
            tradingExchangeOrder.warnings = [];
        }

        if (tradingExchangeOrder.infos == null) {
            tradingExchangeOrder.infos = [];
        }
        if (tradingExchangeOrder.tradeType === TradeType.EQUITY) {
            return this.doEquityCompliance(tradingExchangeOrder);
        }

        if (tradingExchangeOrder.tradeType === TradeType.OPTION) {
            return this.doOptionCompliance(tradingExchangeOrder);
        }
        return true;
    }

    public doEquityCompliance(tradingExchangeOrder: TradingExchangeOrder): boolean {
        const selectedAddlOrdData: AddlOrdData[] = tradingExchangeOrder.addlOrdData;
        const applicableAddlOrdData: AddlOrdData[] = this.getApplicableAddlOrdData(tradingExchangeOrder);
        let isValid = true;
        for (let selected of selectedAddlOrdData) {
            if (applicableAddlOrdData.indexOf(selected) === -1) {
                isValid = false;
                this.handleTradingExchangeOrderErrors(selected,tradingExchangeOrder);  
            }
        }
        return isValid;
    }

    private handleTradingExchangeOrderErrors (selected,tradingExchangeOrder):void
    { 
        if (selected === AddlOrdData.AON) {
            tradingExchangeOrder.errors.push(this.INVALID_AON_FOR_EQUITY);
        }
        if (selected === AddlOrdData.NOTHELD) {
            tradingExchangeOrder.errors.push(this.INVALID_NOTHELD_FOR_EQUITY);
        }
        if (selected === AddlOrdData.DONOTREDUCE) {
            tradingExchangeOrder.errors.push(this.INVALID_DONOTREDUCE_FOR_EQUITY);
        }
        if (selected === AddlOrdData.DONOTINCREASE) {
            tradingExchangeOrder.errors.push(this.INVALID_DONOTINCREASE_FOR_EQUITY);
        }
        if (selected === AddlOrdData.CASHSAMEDAY) {
            tradingExchangeOrder.errors.push(this.INVALID_CASHSAMEDAY_FOR_EQUITY);
        }
        if (selected === AddlOrdData.NEXTDAY) {
            tradingExchangeOrder.errors.push(this.INVALID_NEXTDAY_FOR_EQUITY);
        }
        if (selected === AddlOrdData.EXECUTEASAGENT) {
            tradingExchangeOrder.errors.push(this.INVALID_EXECUTEASAGENT_FOR_EQUITY);
        }
    }

    public doOptionCompliance(tradingExchangeOrder: TradingExchangeOrder): boolean {
        const selectedAddlOrdData: AddlOrdData[] = tradingExchangeOrder.addlOrdData;
        const applicableAddlOrdData: AddlOrdData[] = this.getOptionsApplicableAddlOrdData(tradingExchangeOrder);
        let isValid = true;
        for (let selected of selectedAddlOrdData) {
            if (applicableAddlOrdData.indexOf(selected) === -1) {
                isValid = false;
                if (selected === AddlOrdData.AON) {
                    tradingExchangeOrder.errors.push(this.INVALID_AON_FOR_OPTION);
                }
                if (selected === AddlOrdData.NOTHELD) {
                    tradingExchangeOrder.errors.push(this.INVALID_NOTHELD_FOR_OPTION);
                }
                if (selected === AddlOrdData.EXECUTEASAGENT) {
                    tradingExchangeOrder.errors.push(this.INVALID_EXECUTEASAGENT_FOR_OPTION);
                }
            }
        }
        return isValid;
    }

    public setDisabledVals(selectedVals: string[]): void {
        //let tradeConditions = this.equityTradingOrderAdditionalInfoForm.get('addlOrdData') as FormControl;
        //let selectedConditions = tradeConditions.value as string[];
        if (!!selectedVals && selectedVals.length > 0) {
            let newApplicableAddlOrdData: Map<string, boolean> = this.getApplicableAddlOrdDataBaseOnSelected(selectedVals);
            if (!!newApplicableAddlOrdData) {
                this.isDoNotIncreaseDisabled = newApplicableAddlOrdData.get(AddlOrdData.DONOTINCREASE);
                this.isDoNotReduceDisabled = newApplicableAddlOrdData.get(AddlOrdData.DONOTREDUCE);
                this.isNextDayDisabled = newApplicableAddlOrdData.get(AddlOrdData.NEXTDAY);
                this.isCashSameDayDisabled = newApplicableAddlOrdData.get(AddlOrdData.CASHSAMEDAY);
            }
        } else {
            this.isDoNotIncreaseDisabled = false;
            this.isDoNotReduceDisabled = false;
            this.isNextDayDisabled = false;
            this.isCashSameDayDisabled = false;
        }
    }

    public getOrderQualifierVals(
        tradingEnt: TradingEntitlements,
        tradingExchangeOrder: TradingExchangeOrder,
        selectedVals: string[]): string[] {

        if ((tradingExchangeOrder.tradeType === TradeType.EQUITY && !tradingExchangeOrder.ordQty)
        || !tradingExchangeOrder.transactionType
        || !tradingExchangeOrder.orderType
        || !tradingExchangeOrder.duration) {
            this.isOrderQualifierEnable = false;
            return null;
        }

        let newSelectedVals: string[] = null;
        this.tradingEntitlements = tradingEnt;
        let applicableVals = this.getApplicableAddlOrdData(tradingExchangeOrder);

        if (!!applicableVals) {
            this.isAonAvailable = applicableVals.indexOf(AddlOrdData.AON) !== -1;
            this.isNotHeldAvailable = applicableVals.indexOf(AddlOrdData.NOTHELD) !== -1;
            this.isDoNotIncreaseAvailable = applicableVals.indexOf(AddlOrdData.DONOTINCREASE) !== -1;
            this.isDoNoReduceAvailable = applicableVals.indexOf(AddlOrdData.DONOTREDUCE) !== -1;
            this.isNextDayAvailable = applicableVals.indexOf(AddlOrdData.NEXTDAY) !== -1;
            this.isCashSameDayAvailable = applicableVals.indexOf(AddlOrdData.CASHSAMEDAY) !== -1;
            this.isExecuteAsagentAvailable = applicableVals.indexOf(AddlOrdData.EXECUTEASAGENT) !== -1;
        }

        // Must clear any selections that are about to be removed from the drop-down
        if (!!selectedVals) {
            newSelectedVals = this.handleSelectedVals(selectedVals);
        } else if (this.isDoNoReduceAvailable && this.isDoNotReduceRequired) {
            // set DONOTREDUCE as required
            newSelectedVals = [AddlOrdData.DONOTREDUCE];
        }
        if (applicableVals.length === 0) {
            this.isOrderQualifierEnable = false;
        } else {
            this.isOrderQualifierEnable = true;
        }
        return newSelectedVals;
    }


    private handleSelectedVals(selectedVals):any
    {
        if (this.validateAddlOrdData(selectedVals.indexOf(AddlOrdData.AON),this.isAonAvailable)) {
            selectedVals.splice(selectedVals.indexOf(AddlOrdData.AON), 1);
        }
        if (this.validateAddlOrdData(selectedVals.indexOf(AddlOrdData.NOTHELD),this.isNotHeldAvailable)) {
            selectedVals.splice(selectedVals.indexOf(AddlOrdData.NOTHELD), 1);
        }
        if (this.validateAddlOrdData(selectedVals.indexOf(AddlOrdData.DONOTINCREASE),this.isDoNotIncreaseAvailable)) {
            selectedVals.splice(selectedVals.indexOf(AddlOrdData.DONOTINCREASE), 1);
        }
        if (this.validateAddlOrdData(selectedVals.indexOf(AddlOrdData.DONOTREDUCE),this.isDoNoReduceAvailable)) {
            selectedVals.splice(selectedVals.indexOf(AddlOrdData.DONOTREDUCE), 1);
        }
        if (this.validateAddlOrdData(selectedVals.indexOf(AddlOrdData.NEXTDAY),this.isNextDayAvailable)) {
            selectedVals.splice(selectedVals.indexOf(AddlOrdData.NEXTDAY), 1);
        }
        if (this.validateAddlOrdData(selectedVals.indexOf(AddlOrdData.CASHSAMEDAY),this.isCashSameDayAvailable)) {
            selectedVals.splice(selectedVals.indexOf(AddlOrdData.CASHSAMEDAY), 1);
        }
        if (this.validateAddlOrdData(selectedVals.indexOf(AddlOrdData.EXECUTEASAGENT),this.isExecuteAsagentAvailable)) {
            selectedVals.splice(selectedVals.indexOf(AddlOrdData.EXECUTEASAGENT), 1);
        }
        if (this.isDoNoReduceAvailable && this.isDoNotReduceRequired && selectedVals.indexOf(AddlOrdData.DONOTREDUCE) === -1) {
            selectedVals.splice(1, 0, AddlOrdData.DONOTREDUCE);
        }
        if (selectedVals.length === 0) {
            // null out the array if we end up clearing all values, to avoid showing the Order
            // Qualifier field in the review dialog if the user doesn't make any new selections
            selectedVals = null;
        }
        return selectedVals;
    }

    public validateAddlOrdData(indexOf:number,isAvailable:boolean):boolean{
        return indexOf !== -1 && !isAvailable
    }
/**
	* Get the list of Applicable 'Additional Order Data' Options based on the selection of tradeType, transactionType, orderType, orderDuration,orderTradeExchange and userType.
	*/
    public getOptionsApplicableAddlOrdData(tradingExchangeOrder: TradingExchangeOrder): AddlOrdData[] {
        const applicableAddlOrdData: AddlOrdData[] = [];
        const orderType: OrderType = tradingExchangeOrder.orderType;
        const orderDuration: OrderDuration = tradingExchangeOrder.duration;
        let isExecuteAsAgentOptionEnabled = false;
        if (!!tradingExchangeOrder.isExecuteAsAgentOptionEnabled) {
            isExecuteAsAgentOptionEnabled = tradingExchangeOrder.isExecuteAsAgentOptionEnabled;
        }
        // let isExecuteAsAgentOptionEnabled: boolean = tradingExchangeOrder.isExecuteAsAgentOptionEnabled;
        if (this.isAonValidOption(orderType, orderDuration)) {
            applicableAddlOrdData.push(AddlOrdData.AON);
        }
        if (this.isNotHeldValidOption(orderType, orderDuration)) {
            applicableAddlOrdData.push(AddlOrdData.NOTHELD);
        }
        if (this.isExecuteAsAgentValidOption(orderType, orderDuration, isExecuteAsAgentOptionEnabled)) {
            applicableAddlOrdData.push(AddlOrdData.EXECUTEASAGENT);
        }
        return applicableAddlOrdData;
    }

/**
	* Return is 'AON' option is a valid based on the selection of orderType, orderDuration
	*/
    private isAonValidOption(orderType: OrderType, orderDuration: OrderDuration): boolean {
        if (orderType !== OrderType.LIMIT) {
            return false;
        }
        if (this.optionsAllowedDurationListAON.indexOf(orderDuration) === -1) {
            return false;
        }
        return true;
    }

/**
	* Return is 'NotHeld' option is a valid based on the selection of orderType, orderDuration
	*/
    private isNotHeldValidOption(orderType: OrderType, orderDuration: OrderDuration): boolean {
        if (orderDuration !== OrderDuration.DAY) {
            return false;
        }
        if (this.optionsAllowedOrderTypeListNH.indexOf(orderType) === -1) {
            return false;
        }
        return true;
    }

/**
	* Return is 'NotHeld' option is a valid based on the selection of orderType, orderDuration
	*/
    private isExecuteAsAgentValidOption(orderType: OrderType, orderDuration: OrderDuration, isExecuteAsAgentOptionEnabled: boolean): boolean {
        if (!isExecuteAsAgentOptionEnabled) {
            return false;
        }
        if (this.optionsAllowedDurationListEAA.indexOf(orderDuration) === -1) {
            return false;
        }
        if (this.optionsAllowedOrderTypeListEAA.indexOf(orderType) === -1) {
            return false;
        }
        return true;
    }

/**
	* Get the list of Applicable 'Additional Order Data' Options based on the selection of tradeType, transactionType, orderType, orderDuration,orderTradeExchange and userType.
	*/
    public getApplicableAddlOrdData(tradingExchangeOrder: TradingExchangeOrder): AddlOrdData[] {
        const applicableAddlOrdData: AddlOrdData[] = [];
        const tradeType: TradeType = tradingExchangeOrder.tradeType;
        const transactionType: TransactionType = tradingExchangeOrder.transactionType;
        const orderType: OrderType = tradingExchangeOrder.orderType;
        const orderDuration: OrderDuration = tradingExchangeOrder.duration;
        let isOTC: boolean = false;
        let isIntlSecurity: boolean = false;
        const isExecuteAsAgentEnabled: boolean = tradingExchangeOrder.isExecuteAsAgentEnabled;
        const userType: UserType = this.getUserType();
        const isQtyLessthanOneHundred: boolean = tradingExchangeOrder.ordQty < 100 ? true : false;
        if (!!tradingExchangeOrder.quote) {
            isIntlSecurity = !!tradingExchangeOrder.quote.qtIntlInd && tradingExchangeOrder.quote.qtIntlInd === 'Y';
            if ((tradeType === TradeType.EQUITY)) {
                isOTC = this.tradeExchangeListForOTC.indexOf(tradingExchangeOrder.quote.tradeExchange) !== -1;
            }
        }
        if (this.isAonValid(orderType, orderDuration, isIntlSecurity, transactionType, isQtyLessthanOneHundred)) {
            applicableAddlOrdData.push(AddlOrdData.AON);
        }
        if (this.isNotHeldValid(orderType, orderDuration, isOTC, isIntlSecurity, transactionType, isQtyLessthanOneHundred)) {
            applicableAddlOrdData.push(AddlOrdData.NOTHELD);
        }
        if (this.isDoNotReduceValid(transactionType, orderType, orderDuration, userType, isIntlSecurity)) {
            applicableAddlOrdData.push(AddlOrdData.DONOTREDUCE);
        }
        if (this.isDoNotIncreaseValid(transactionType, orderType, orderDuration, userType, isIntlSecurity)) {
            applicableAddlOrdData.push(AddlOrdData.DONOTINCREASE);
        }
        if (this.isCashSameDayValid(transactionType, orderType, orderDuration, isIntlSecurity, isQtyLessthanOneHundred)) {
            applicableAddlOrdData.push(AddlOrdData.CASHSAMEDAY);
        }
        if (this.isNextDayValid(transactionType, orderType, orderDuration, isIntlSecurity)) {
            applicableAddlOrdData.push(AddlOrdData.NEXTDAY);
        }
        if (this.isExecuteAsAgentValid(orderType, orderDuration, isIntlSecurity, isExecuteAsAgentEnabled, transactionType, isQtyLessthanOneHundred)) {
            applicableAddlOrdData.push(AddlOrdData.EXECUTEASAGENT);
        }
        return applicableAddlOrdData;
    }
/**
	* get the new Applicable list of 'Additional Order Data' options based on the selection of some of the values of the applicable list.
	*/
    public getApplicableAddlOrdDataBaseOnSelected(selectedAddlOrdData: string[]): Map<string, boolean> {
        const newApplicableAddlOrdDataMap = new Map();
        newApplicableAddlOrdDataMap.set(AddlOrdData.DONOTINCREASE, false);
        newApplicableAddlOrdDataMap.set(AddlOrdData.DONOTREDUCE, false);
        newApplicableAddlOrdDataMap.set(AddlOrdData.NEXTDAY, false);
        newApplicableAddlOrdDataMap.set(AddlOrdData.CASHSAMEDAY, false);
        if (((selectedAddlOrdData === null) || selectedAddlOrdData.length === 0)) {
            return newApplicableAddlOrdDataMap;
        }

        if (selectedAddlOrdData.indexOf(AddlOrdData.DONOTINCREASE) !== -1) {
            newApplicableAddlOrdDataMap.set(AddlOrdData.DONOTREDUCE, true);
            newApplicableAddlOrdDataMap.set(AddlOrdData.NEXTDAY, true);
            newApplicableAddlOrdDataMap.set(AddlOrdData.CASHSAMEDAY, true);
        } else if (selectedAddlOrdData.indexOf(AddlOrdData.DONOTREDUCE) !== -1) {
            newApplicableAddlOrdDataMap.set(AddlOrdData.DONOTINCREASE, true);
            newApplicableAddlOrdDataMap.set(AddlOrdData.NEXTDAY, true);
            newApplicableAddlOrdDataMap.set(AddlOrdData.CASHSAMEDAY, true);
        } else if (selectedAddlOrdData.indexOf(AddlOrdData.NEXTDAY) !== -1) {
            newApplicableAddlOrdDataMap.set(AddlOrdData.DONOTINCREASE, true);
            newApplicableAddlOrdDataMap.set(AddlOrdData.DONOTREDUCE, true);
            newApplicableAddlOrdDataMap.set(AddlOrdData.CASHSAMEDAY, true);
        } else if (selectedAddlOrdData.indexOf(AddlOrdData.CASHSAMEDAY) !== -1) {
            newApplicableAddlOrdDataMap.set(AddlOrdData.DONOTINCREASE, true);
            newApplicableAddlOrdDataMap.set(AddlOrdData.DONOTREDUCE, true);
            newApplicableAddlOrdDataMap.set(AddlOrdData.NEXTDAY, true);
        }
        return newApplicableAddlOrdDataMap;
    }

/**
	* Return is 'AON' option is a valid based on the selection of orderType, orderDuration, isIntlSecurity
	*/
    private isAonValid(orderType: OrderType, orderDuration: OrderDuration, isIntlSecurity: boolean, transactionType: TransactionType, isQtyLessthanOneHundred: boolean): boolean {
        if (isIntlSecurity) {
            return false;
        }
        if (this.invalidOrderTypeListForAON.indexOf(orderType) !== -1) {
            return false;
        }
        if ((orderDuration === OrderDuration.IOC || orderDuration === OrderDuration.FOK)) {
            return false;
        }
        if (isQtyLessthanOneHundred &&
            (this.sellTransactionTypeList.indexOf(transactionType) !== -1 ||
                this.buyTransactionTypeList.indexOf(transactionType) !== -1)) {
            if (this.orderTypeListForLimits.indexOf(orderType) !== -1 &&
                this.durationListAONQuantity.indexOf(orderDuration) !== -1) {
                return false;
            } else if (orderType === OrderType.EXECUTE_AT_CLOSE &&
                orderDuration === OrderDuration.DAY) {
                return false;
            }
        }
        return true;
    }

/**
	* Return is 'NotHeld' option is a valid based on the selection of orderType, orderDuration, isOTC
	*/
    private isNotHeldValid(orderType: OrderType, orderDuration: OrderDuration, isOTC: boolean, isIntlSecurity: boolean,
                           transactionType: TransactionType, isQtyLessthanOneHundred: boolean): boolean {
        if (!isIntlSecurity) {
           return this.handleisNotIntlSecurity(orderType,orderDuration,isOTC,isQtyLessthanOneHundred,transactionType);
        } else {
            if (!isQtyLessthanOneHundred && orderDuration === OrderDuration.DAY
                && this.orderTypeListNHQuantity.indexOf(orderType) !== -1
                && (this.sellTransactionTypeList.indexOf(transactionType) !== -1 ||
                    this.buyTransactionTypeList.indexOf(transactionType) !== -1)) {
                return true;
            }
            return false;
        }
    }

    private handleisNotIntlSecurity(orderType,orderDuration,isOTC,isQtyLessthanOneHundred,transactionType):boolean
    {
        if (this.allowedOrderTypeListForNH.indexOf(orderType) === -1) {
            return false;
        }
        if (this.allowedDurationListForNH.indexOf(orderDuration) === -1) {
            return false;
        }
        if (!isOTC && this.durationListForGoodTill.indexOf(orderDuration) !== -1) {
            return false;
        }
        if (isOTC
            && (orderType === OrderType.MARKET || orderType === OrderType.LIMIT_ON_CLOSE)
            && this.durationListForGoodTill.indexOf(orderDuration) !== -1) {
            return false;
        }
        if (orderType === OrderType.LIMIT_ON_CLOSE && orderDuration === OrderDuration.DAY) {
            return false;
        }
        if (isQtyLessthanOneHundred &&
            this.orderTypeListNHQuantity.indexOf(orderType) !== -1 &&
            orderDuration === OrderDuration.DAY &&
            (this.sellTransactionTypeList.indexOf(transactionType) !== -1 ||
            this.buyTransactionTypeList.indexOf(transactionType) !== -1)) {
            return false;
        }
        return true;
    }

/**
	* Return is 'DoNotReduce' option a valid based on the selection of transactionType, orderType, orderDuration, userType
	*/
    private isDoNotReduceValid(transactionType: TransactionType, orderType: OrderType, orderDuration: OrderDuration, userType: UserType, isIntlSecurity: boolean): boolean {
        if (!isIntlSecurity) {
            return this.handleisNotIntlSecurityIfisDoNotReduceValid(orderDuration,userType,orderType,transactionType);
        } else {
            if ((orderType === OrderType.LIMIT || orderType === OrderType.LIMIT_ON_CLOSE)
                && this.durationListForGtcGtd.indexOf(orderDuration) !== -1
                && this.buyTransactionTypeList.indexOf(transactionType) !== -1
            ) {
                this.isDoNotReduceRequired = true;
                return true;
            }
            this.isDoNotReduceRequired = false;
            return false;
        }
    }

    private handleisNotIntlSecurityIfisDoNotReduceValid(orderDuration,userType,orderType,transactionType):boolean
    {
        if (this.durationListForGoodTill.indexOf(orderDuration) === -1) {
            return false;
        }
        if (userType === UserType.RIA && orderDuration === OrderDuration.GTM) {
            return false;
        }
        if (orderType === OrderType.MARKET) {
            return false;
        }
        // The validation for durationListForGoodTill was already done
        if (this.sellTransactionTypeList.indexOf(transactionType) !== -1 && this.orderTypeListForCSDAndEAA.indexOf(orderType) !== -1) {
            return false;
        }
        if (this.buyTransactionTypeList.indexOf(transactionType) !== -1 && this.orderTypeListForLimits.indexOf(orderType) === -1) {
            return false;
        }
        return true;
    }

/**
	* Return is 'DoNotIncrease' option is valid based on the selection of transactionType, orderType, orderDuration, userType, isIntlSecurity
	*/
    private isDoNotIncreaseValid(
        transactionType: TransactionType, orderType: OrderType, orderDuration: OrderDuration, userType: UserType,
        isIntlSecurity: boolean): boolean {
        if (isIntlSecurity) {
            return false;
        }
        if (this.durationListForGoodTill.indexOf(orderDuration) === -1) {
            return false;
        }
        if (userType === UserType.RIA && this.invalidDurationListForDNIAndRIA.indexOf(orderDuration) !== -1) {
            return false;
        }
        if (orderType === OrderType.MARKET) {
            return false;
        }
        // The validation for durationListForGoodTill was already done
        if (this.sellTransactionTypeList.indexOf(transactionType) !== -1) {
            return false;
        }
        if (this.buyTransactionTypeList.indexOf(transactionType) !== -1 && this.orderTypeListForLimits.indexOf(orderType) === -1) {
            return false;
        }
        return true;
    }

/**
	* Return is 'CashSameDay' option is valid based on the selection of transactionType, orderType, orderDuration, isIntlSecurity, isOTC
	*/
    private isCashSameDayValid(
        transactionType: TransactionType, orderType: OrderType, orderDuration: OrderDuration,
        isIntlSecurity: boolean, isQtyLessthanOneHundred: boolean): boolean {
        if (isIntlSecurity) {
            return false;
        }
        if (orderDuration === OrderDuration.FOK) {
            return false;
        }
        if ((orderDuration === OrderDuration.OPG || orderDuration === OrderDuration.IOC)
            && this.invalidOrderTypeListForCSD.indexOf(orderType) !== -1) {
            return false;
        }
        if (orderType === OrderType.MARKET && this.allowedDurationListForCSDA.indexOf(orderDuration) === -1) {
            return false;
        }
        if (this.durationListForGoodTill.indexOf(orderDuration) !== -1) {
            return false;
        }
        if (transactionType === TransactionType.BUY || transactionType === TransactionType.BUY_TO_COVER) {
            return false;
        }
        if (isQtyLessthanOneHundred &&
            this.orderTypeListForLimits.indexOf(orderType) !== -1 &&
            orderDuration === OrderDuration.IOC &&
            this.sellTransactionTypeList.indexOf(transactionType) !== -1 ) {
            return false;
        }
        return true;
    }

/**
	* Return is 'NextDay' option is valid based on the selection of transactionType, orderType, orderDuration, isIntlSecurity, isOTC
	*/
    private isNextDayValid(transactionType: TransactionType, orderType: OrderType, orderDuration: OrderDuration, isIntlSecurity: boolean): boolean {
        if (isIntlSecurity) {
            return false;
        }
        if (this.durationListForGoodTill.indexOf(orderDuration) !== -1) {
            return false;
        }
        if (orderDuration === OrderDuration.FOK || orderDuration === OrderDuration.IOC) {
            return false;
        }
        if (orderDuration === OrderDuration.OPG && this.orderTypeListForNDSAndEAA.indexOf(orderType) === -1) {
            return false;
        }
        if (transactionType === TransactionType.BUY || transactionType === TransactionType.BUY_TO_COVER) {
            return false;
        }
        return true;
    }

   /**
    * Return is 'ExecuteAsAgent' option is valid based on the selection of orderType, orderDuration, isIntlSecurity, isExecuteAsAgentEnabled
    */
    private isExecuteAsAgentValid(
        orderType: OrderType, orderDuration: OrderDuration, isIntlSecurity: boolean, isExecuteAsAgentEnabled: boolean,
        transactionType: TransactionType, isQtyLessthanOneHundred: boolean): boolean {
        if (!isExecuteAsAgentEnabled) {
            return false;
        }
        if (!isIntlSecurity) {
            return this.handleisNotIntlSecurityIfExecuteAsAgentEnabled(orderDuration,orderType,isQtyLessthanOneHundred,transactionType);
        } else {
            return this.handleisIntlSecurityIfExecuteAsAgentEnabled(orderDuration,orderType,transactionType);
        }
    }

    private handleisIntlSecurityIfExecuteAsAgentEnabled(orderDuration,orderType,transactionType):boolean
    {
        if (this.buyTransactionTypeList.indexOf(transactionType) !== -1) {
            if (orderDuration === OrderDuration.DAY && this.orderTypeListForNDSAndEAA.indexOf(orderType) !== -1) {
                return true;
            }
            if (orderType === OrderType.EXECUTE_AT_CLOSE
                && this.durationListForGtcGtd.indexOf(orderDuration) !== -1) {
                return true;
            }
        }
        if (this.sellTransactionTypeList.indexOf(transactionType) !== -1) {
            if (orderDuration === OrderDuration.DAY && this.orderTypeListForNDSAndEAA.indexOf(orderType) !== -1) {
                return true;
            }
            if (this.durationListForGtcGtd.indexOf(orderDuration) !== -1 && this.orderTypeListForCSDAndEAA.indexOf(orderType) !== -1) {
                return true;
            }
        }
        return false;

    }

    private handleisNotIntlSecurityIfExecuteAsAgentEnabled(orderDuration,orderType,isQtyLessthanOneHundred,transactionType):boolean
    {
        if ((orderDuration === OrderDuration.FOK || orderDuration === OrderDuration.IOC) && this.orderTypeListForCSDAndEAA.indexOf(orderType) === -1) {
            return false;
        }
        if (orderDuration === OrderDuration.OPG && this.orderTypeListForNDSAndEAA.indexOf(orderType) === -1) {
            return false;
        }
        if (orderType === OrderType.MARKET && this.durationListForGoodTill.indexOf(orderDuration) !== -1) {
            return false;
        }
        if (isQtyLessthanOneHundred &&
            this.orderTypeListForLimits.indexOf(orderType) !== -1 &&
            this.notAllowedDurationListForQuantity.indexOf(orderDuration) !== -1 &&
            (this.sellTransactionTypeList.indexOf(transactionType) !== -1 ||
            this.buyTransactionTypeList.indexOf(transactionType) !== -1)) {
            return false;
        }
        return true;
    }

    setTradingEntitlements(tradingEntitlements: TradingEntitlements) {
        this.tradingEntitlements = tradingEntitlements;
    }

    public getUserType(): UserType {
        if (this.tradingEntitlements.isRiaUser) {
            return UserType.RIA;
        } else if (this.tradingEntitlements.isBdUser) {
            return UserType.BD;
        } else if (this.tradingEntitlements.isBdDeskUser) {
            return UserType.DUAL;
        }
    }
}
